// Kinio 雲端設定（app/home-base docs/KINIO-API.md §0）。
//
// SDK 不讀 Vite env：使用端（home-base、vms、web/partner）在啟動時呼叫 configureKinio() 注入品牌、client_id、
// identity base（home-base 另注入 prod / dev 兩個 identity、釘住的 identity、未登入的 api base，見 home-base web/src/lib/kinioBoot.ts）。
// plane api base **不在這裡**——由登入回傳的 homes[].endpoints.api 決定（planeBase()）。
//
// Dev proxy：使用端的 dev server 可把 /kinio-id → identity、/kinio-api/<host> → https://<host> 同源代理；devProxy = true 時
// identityUrl / planeBase 改寫成同源路徑（devProxyIdentityHost = true 時 identity 也帶主機：/kinio-id/<host>，切雲端環境時代理目標跟著換）。
// production 一律直打（CORS）。

import { CLOUD_ENV_STORAGE_KEY, cloudEnvLabel, parseCloudEnv, trimBase as trimSlash, type CloudEnv, type CloudEnvLabel } from './cloudEnv';

/** 同源代理路徑前綴（與使用端 dev server 的 proxy 設定一致）。 */
export const DEV_PROXY_ID_PREFIX = '/kinio-id';
export const DEV_PROXY_API_PREFIX = '/kinio-api';

export interface KinioConfig {
  /** identity `brand`（無品牌 = kinio）。identity 以 client 為準，這個只是顯示與相容。 */
  brandId: string;
  /** 顯示名。 */
  brandName: string;
  /** identity `client_id`。 */
  clientId: string;
  /** 目前用的 identity base URL（不含尾斜線）；切雲端環境時 applyCloudEnv() 換掉。 */
  identityBase: string;
  /** 可切換的 prod / dev identity（home-base：品牌注入）；沒注入 = 空字串（不能切）。 */
  identityBases: { prod: string; dev: string };
  /** 釘住的 identity（部署佔位 / 啟動參數覆寫）；空 = 沒釘住，可切。 */
  pinnedIdentityBase: string;
  /** 開發版建置。 */
  devBuild: boolean;
  /** 目前選的雲端環境；釘住或沒用環境切換時 null（看 currentCloudEnvLabel()）。 */
  cloudEnv: CloudEnv | null;
  /** App 對外版本（`X-Kinio-SDK: web/<semver>` 用；空 = 不帶這個標頭）。 */
  appVersion: string;
  /** dev 同源代理是否啟用。 */
  devProxy: boolean;
  /** dev 代理的 identity 路徑帶主機（/kinio-id/<host>；home-base 的 vite 代理）；false = /kinio-id（vms）。 */
  devProxyIdentityHost: boolean;
  /**
   * 不登入時打的 plane api base（「掃貼紙救援」`POST /v1/recovery/sessions`，SPEC #contract-app-recovery）：
   * 登入後一律用 homes[].endpoints.api；登入前沒有家戶 → 品牌注入的出貨 plane。空 → 呼叫端回 CONFIG_MISSING。
   */
  preLoginApiBase: string;
  /** dev 環境的未登入 api base（「建立帳號並加入」照邀請連結 / 目前雲端環境選 plane）；沒設 → 退回 preLoginApiBase。 */
  preLoginApiBaseDev: string;
}

export const kinioConfig: KinioConfig = {
  brandId: 'kinio',
  brandName: 'Kinio',
  clientId: 'c-kinio-web',
  identityBase: '', // 一律由使用端注入（SDK 不寫死主機，SPEC #public-api C6 PAPI-06）
  identityBases: { prod: '', dev: '' },
  pinnedIdentityBase: '',
  devBuild: false,
  cloudEnv: null,
  appVersion: '',
  devProxy: false,
  devProxyIdentityHost: false,
  preLoginApiBase: '',
  preLoginApiBaseDev: '',
};

const TRIMMED: ReadonlySet<keyof KinioConfig> = new Set(['identityBase', 'pinnedIdentityBase', 'preLoginApiBase', 'preLoginApiBaseDev']);

/** 注入設定（部分覆寫；undefined / 空字串視為沒給，null 照設）。 */
export function configureKinio(p: Partial<KinioConfig>): KinioConfig {
  for (const [k, v] of Object.entries(p) as [keyof KinioConfig, unknown][]) {
    if (v === undefined || v === '') continue;
    let val = v;
    if (TRIMMED.has(k)) val = trimSlash(String(v).trim());
    else if (k === 'identityBases') {
      const b = v as { prod?: string; dev?: string };
      val = { prod: trimSlash(b.prod), dev: trimSlash(b.dev) };
    }
    (kinioConfig as unknown as Record<string, unknown>)[k] = val;
  }
  return kinioConfig;
}

/** 本機存的雲端環境（localStorage kinio.cloud_env）；讀不到 = null。 */
export function readStoredCloudEnv(): CloudEnv | null {
  try {
    return parseCloudEnv(globalThis.localStorage?.getItem(CLOUD_ENV_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function storeCloudEnv(next: CloudEnv): void {
  try {
    globalThis.localStorage?.setItem(CLOUD_ENV_STORAGE_KEY, next);
  } catch {
    /* 隱私模式 / 無 storage：只在這次執行有效 */
  }
}

/** 換雲端環境的 identity（不存、不登出；home-base 的完整流程在 cloudEnvSwitch.ts switchCloudEnv）。釘住或沒注入該環境時不動。 */
export function applyCloudEnv(next: CloudEnv): void {
  if (kinioConfig.pinnedIdentityBase || !kinioConfig.identityBases[next]) return;
  kinioConfig.cloudEnv = next;
  kinioConfig.identityBase = kinioConfig.identityBases[next];
}

/**
 * 未登入的 api base（建立帳號並加入用）：env（邀請連結的 env；沒帶 → 目前雲端環境，釘住時看釘住 identity 的標籤）是 dev 且有
 * preLoginApiBaseDev → 它；其他 → preLoginApiBase（出貨 plane）。
 */
export function preLoginApiBaseFor(env?: string | null): string {
  const e = env || kinioConfig.cloudEnv || (currentCloudEnvLabel() === 'dev' ? 'dev' : 'prod');
  if (e === 'dev' && kinioConfig.preLoginApiBaseDev) return kinioConfig.preLoginApiBaseDev;
  return kinioConfig.preLoginApiBase;
}

/** 目前 identity 的環境標籤（頂端色條、設備資訊）；prod → null。 */
export function currentCloudEnvLabel(): CloudEnvLabel {
  return cloudEnvLabel(kinioConfig.identityBase, kinioConfig.identityBases);
}

/** identity URL。dev proxy 時走同源 /kinio-id（devProxyIdentityHost：/kinio-id/<host>，代理只認注入的幾個 identity 主機）。 */
export function identityUrl(path: string): string {
  const base = kinioConfig.identityBase;
  if (!kinioConfig.devProxy) return base + path;
  if (!kinioConfig.devProxyIdentityHost) return DEV_PROXY_ID_PREFIX + path;
  try {
    const u = new URL(base);
    return `${DEV_PROXY_ID_PREFIX}/${u.host}${trimSlash(u.pathname)}${path}`;
  } catch {
    return base + path;
  }
}

/**
 * plane api base：homes[].endpoints.api（絕對 URL）。dev proxy 時改寫成
 * `/kinio-api/<host>`，由使用端 dev server 轉送到 https://<host>。
 */
export function planeBase(apiEndpoint: string): string {
  const base = trimSlash(apiEndpoint.trim());
  if (!kinioConfig.devProxy) return base;
  try {
    const u = new URL(base);
    return `${DEV_PROXY_API_PREFIX}/${u.host}${trimSlash(u.pathname)}`;
  } catch {
    return base;
  }
}
